/**
 * ComparisonModal Component
 *
 * Modal dialog for selecting comparison mode in the SPLH Dashboard.
 * Displays available comparison options with descriptions.
 *
 * Features:
 * - Radio button selection for comparison modes
 * - Descriptions for each mode
 * - Purple theme matching dashboard design
 * - Cancel and Apply buttons
 *
 * @module pages/common/dashboards/SPLHDashboard/components/ComparisonModal
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { COMPARISON_MODES } from '../../hooks/useSPLHDataV2';

/**
 * Comparison mode options with labels and descriptions
 * @constant {Array}
 */
const COMPARISON_OPTIONS = [
  {
    value: COMPARISON_MODES.PREVIOUS_PERIOD,
    label: 'Previous Period',
    description: 'Compare to the same length period immediately before',
  },
  {
    value: COMPARISON_MODES.PREVIOUS_MONTH,
    label: 'Previous Month',
    description: 'Compare to the same dates in the previous month',
  },
  {
    value: COMPARISON_MODES.PREVIOUS_YEAR,
    label: 'Previous Year',
    description: 'Compare to the same dates in the previous year',
  },
];

/**
 * ComparisonModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.currentMode - Currently selected comparison mode
 * @param {Function} props.onSubmit - Callback when selection is submitted
 * @returns {React.Component} ComparisonModal component
 */
const ComparisonModal = ({ open, onClose, currentMode, onSubmit }) => {
  const [selectedMode, setSelectedMode] = useState(currentMode);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedMode(currentMode);
    }
  }, [open, currentMode]);

  /**
   * Handles apply button click
   */
  const handleApply = () => {
    onSubmit(selectedMode);
  };

  /**
   * Handles cancel button click
   */
  const handleCancel = () => {
    setSelectedMode(currentMode);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CompareIcon sx={{ color: '#7C3AED' }} />
          <Typography variant="h6" fontWeight="medium">
            Compare To
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleCancel} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {COMPARISON_OPTIONS.map((option) => {
          const isSelected = selectedMode === option.value;
          return (
            <Box
              key={option.value}
              sx={{
                border: '1px solid',
                borderColor: isSelected ? '#7C3AED' : '#E5E7EB',
                borderRadius: 1.5,
                mb: 1.5,
                p: 1.5,
                cursor: 'pointer',
                backgroundColor: isSelected ? 'rgba(124, 58, 237, 0.04)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#7C3AED',
                  backgroundColor: 'rgba(124, 58, 237, 0.04)',
                },
              }}
              onClick={() => setSelectedMode(option.value)}
            >
              {/* Line 1: Radio button + Title on same line */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Radio
                  checked={isSelected}
                  value={option.value}
                  sx={{
                    p: 0,
                    mr: 1,
                    color: '#E5E7EB',
                    '&.Mui-checked': {
                      color: '#7C3AED',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={isSelected ? 600 : 500}
                  sx={{ color: isSelected ? '#7C3AED' : 'white' }}
                >
                  {option.label}
                </Typography>
              </Box>
              {/* Line 2: Description */}
              <Typography
                variant="body2"
                sx={{ ml: 4, mt: 0.5, fontSize: '0.75rem', color: 'white' }}
              >
                {option.description}
              </Typography>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <AdminButton variant="text" size="small" onClick={handleCancel}>
          Cancel
        </AdminButton>
        <AdminButton variant="contained" size="small" onClick={handleApply}>
          Apply
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
};

ComparisonModal.propTypes = {
  /** Whether modal is open */
  open: PropTypes.bool.isRequired,
  /** Callback when modal is closed */
  onClose: PropTypes.func.isRequired,
  /** Currently selected comparison mode */
  currentMode: PropTypes.string.isRequired,
  /** Callback when selection is submitted */
  onSubmit: PropTypes.func.isRequired,
};

export default ComparisonModal;
